import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { SalesInsights } from "@/utils/insightsUtils";

const toIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Fetches the full Insights dashboard payload for a date range via the
 * get_sales_insights RPC. One round-trip per range; cached by React Query.
 */
export const useSalesInsights = (start: Date, end: Date) => {
  const getSupabaseClient = useSupabaseClient();
  const startIso = toIsoDate(start);
  const endIso = toIsoDate(end);

  return useQuery<SalesInsights>({
    queryKey: ["sales-insights", startIso, endIso],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");

      // The RPC isn't in the generated Database types yet, so call through an
      // untyped client (same pattern as useRevenuePlays).
      const sb = supabase as unknown as SupabaseClient;
      const { data, error } = await sb.rpc("get_sales_insights", {
        p_start: startIso,
        p_end: endIso,
      });
      if (error) throw error;

      const payload = (data ?? {}) as Partial<SalesInsights>;
      return {
        kpis: payload.kpis ?? null,
        trend: payload.trend ?? [],
        channels: payload.channels ?? [],
        discounts: payload.discounts ?? null,
        dow: payload.dow ?? [],
        pairings: payload.pairings ?? [],
        ladder: payload.ladder ?? [],
        recency: payload.recency ?? [],
        loyalty: payload.loyalty ?? [],
        monitor: payload.monitor ?? null,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
};
